import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Pencil,
  Trash2,
  Building2,
  MapPin,
  Mail,
  Phone,
  Calendar,
  IdCard as IdCardIcon,
  Eye,
  EyeOff,
  Lock as LockIcon,
  Loader2,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  EmployeeFormSheet,
  EmployeeStatusBadge,
  genderLabel,
  fullName,
  initials,
  formatDateHu,
} from "@/components/EmployeeFormSheet";
import {
  SalaryFormSheet,
  formatPayMonth,
} from "@/components/SalaryFormSheet";
import { AccountingFormDialog } from "@/components/AccountingFormDialog";
import { DeductionFormDialog } from "@/components/DeductionFormDialog";
import { BonusFormDialog } from "@/components/BonusFormDialog";
import { CommissionFormDialog } from "@/components/CommissionFormDialog";
import { WorkwearFormDialog } from "@/components/employee/sections/Workwear";
import { DisciplinaryFormDialog } from "@/components/employee/sections/Disciplinary";
import { SalariesTab } from "@/components/employee/SalariesTab";
import { EmploymentTab } from "@/components/employee/EmploymentTab";
import { EmployeeDocumentsTab } from "@/components/employee/EmployeeDocumentsTab";
import { OperationalTab } from "@/components/employee/OperationalTab";
import { NotesTab } from "@/components/employee/NotesTab";
import { PortalAccessTab } from "@/components/employee/PortalAccessTab";
import {
  EmployeeOnboarding,
  isOnboardingDone,
} from "@/components/employee/EmployeeOnboarding";

export default function EmployeeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [company, setCompany] = useState(null);
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Tabs + salaries
  const [activeTab, setActiveTab] = useState("data");
  const [salaries, setSalaries] = useState([]);
  const [salariesLoading, setSalariesLoading] = useState(false);
  const [salaryOpen, setSalaryOpen] = useState(false);

  // Data completeness gate — drives both the locked overlay on the Adatok
  // tab and the disabled state of the Dokumentumok tab. The gate is global
  // for this profile: amíg az adatok hiányosak, a felhasználó nem mehet
  // tovább a dokumentumokra (sem szerződést nem generálhat).
  const [completeness, setCompleteness] = useState(null);
  const refreshCompleteness = async () => {
    if (!id) return;
    try {
      const { data } = await api.get(`/employees/${id}/data-completeness`);
      setCompleteness(data);
    } catch (e) {
      setCompleteness(null);
    }
  };

  // Employee documents — needed at this level to decide whether the
  // onboarding is fully complete (contracts signed + ID photos uploaded).
  const [documents, setDocuments] = useState([]);
  const refreshDocuments = async () => {
    if (!id) return;
    try {
      const { data } = await api.get(`/employees/${id}/documents`);
      setDocuments(Array.isArray(data) ? data : []);
    } catch (e) {
      setDocuments([]);
    }
  };

  // Accounting / payment dialogs
  const [accountingOpen, setAccountingOpen] = useState(false);
  const [accountingSalary, setAccountingSalary] = useState(null);
  const [payingId, setPayingId] = useState(null);

  // Deductions
  const [deductions, setDeductions] = useState([]);
  const [deductionsLoading, setDeductionsLoading] = useState(false);
  const [deductionOpen, setDeductionOpen] = useState(false);
  const [editingDeduction, setEditingDeduction] = useState(null);

  // Bonuses (Operatív)
  const [bonuses, setBonuses] = useState([]);
  const [bonusesLoading, setBonusesLoading] = useState(false);
  const [bonusOpen, setBonusOpen] = useState(false);
  const [approvingBonusId, setApprovingBonusId] = useState(null);
  const [rejectingBonusId, setRejectingBonusId] = useState(null);

  // Commissions (Operatív)
  const [commissions, setCommissions] = useState([]);
  const [commissionsLoading, setCommissionsLoading] = useState(false);
  const [commissionOpen, setCommissionOpen] = useState(false);
  const [approvingCommissionId, setApprovingCommissionId] = useState(null);
  const [rejectingCommissionId, setRejectingCommissionId] = useState(null);

  // Workwear (Operatív)
  const [workwear, setWorkwear] = useState([]);
  const [workwearLoading, setWorkwearLoading] = useState(false);
  const [workwearOpen, setWorkwearOpen] = useState(false);
  const [returningWorkwearId, setReturningWorkwearId] = useState(null);

  // Disciplinaries (Operatív)
  const [disciplinaries, setDisciplinaries] = useState([]);
  const [disciplinariesLoading, setDisciplinariesLoading] = useState(false);
  const [disciplinaryOpen, setDisciplinaryOpen] = useState(false);

  const fetchEmployee = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/employees/${id}`);
      setEmployee(data);
      refreshCompleteness();
      refreshDocuments();
      // Fetch related company/site lazily
      const promises = [];
      if (data.company_id) promises.push(api.get(`/companies/${data.company_id}`));
      else setCompany(null);
      if (data.site_id) promises.push(api.get(`/sites/${data.site_id}`));
      else setSite(null);
      const results = await Promise.allSettled(promises);
      let idx = 0;
      if (data.company_id) {
        const r = results[idx++];
        setCompany(r.status === "fulfilled" ? r.value.data : null);
      }
      if (data.site_id) {
        const r = results[idx++];
        setSite(r.status === "fulfilled" ? r.value.data : null);
      }
    } catch (e) {
      setError(
        formatApiError(e?.response?.data?.detail) ||
          "Nem sikerült betölteni a munkavállalót."
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchSalaries = async () => {
    setSalariesLoading(true);
    try {
      const { data } = await api.get(`/employees/${id}/salaries`);
      setSalaries(data);
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) ||
          "Nem sikerült betölteni a béreket."
      );
    } finally {
      setSalariesLoading(false);
    }
  };

  const fetchDeductions = async () => {
    setDeductionsLoading(true);
    try {
      const { data } = await api.get(`/employees/${id}/deductions`);
      setDeductions(data);
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) ||
          "Nem sikerült betölteni a levonásokat."
      );
    } finally {
      setDeductionsLoading(false);
    }
  };

  const fetchBonuses = async () => {
    setBonusesLoading(true);
    try {
      const { data } = await api.get(`/employees/${id}/bonuses`);
      setBonuses(data);
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) ||
          "Nem sikerült betölteni a bónuszokat."
      );
    } finally {
      setBonusesLoading(false);
    }
  };

  const fetchCommissions = async () => {
    setCommissionsLoading(true);
    try {
      const { data } = await api.get(`/employees/${id}/commissions`);
      setCommissions(data);
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) ||
          "Nem sikerült betölteni a jutalékokat."
      );
    } finally {
      setCommissionsLoading(false);
    }
  };

  const fetchWorkwear = async () => {
    setWorkwearLoading(true);
    try {
      const { data } = await api.get(`/employees/${id}/workwear`);
      setWorkwear(data);
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) ||
          "Nem sikerült betölteni a munkaruhákat."
      );
    } finally {
      setWorkwearLoading(false);
    }
  };

  const fetchDisciplinaries = async () => {
    setDisciplinariesLoading(true);
    try {
      const { data } = await api.get(`/employees/${id}/disciplinaries`);
      setDisciplinaries(data);
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) ||
          "Nem sikerült betölteni a fegyelmi bejegyzéseket."
      );
    } finally {
      setDisciplinariesLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployee();
    fetchSalaries();
    fetchDeductions();
    fetchBonuses();
    fetchCommissions();
    fetchWorkwear();
    fetchDisciplinaries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const upsertSalary = (saved) => {
    if (!saved) return;
    setSalaries((prev) => {
      const idx = prev.findIndex((s) => s._id === saved._id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], ...saved };
        return next;
      }
      return [saved, ...prev].sort((a, b) =>
        (b.pay_month || "").localeCompare(a.pay_month || "")
      );
    });
  };

  const onDeleteSalary = async (salary) => {
    if (
      !window.confirm(
        `Biztosan törlöd a ${formatPayMonth(salary.pay_month)} havi bér rögzítést?`
      )
    )
      return;
    try {
      await api.delete(`/salaries/${salary._id}`);
      setSalaries((prev) => prev.filter((s) => s._id !== salary._id));
      toast.success("Bér törölve.");
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) || "Nem sikerült törölni."
      );
    }
  };

  const onOpenAccounting = (salary) => {
    setAccountingSalary(salary);
    setAccountingOpen(true);
  };

  const onMarkPaid = async (salary) => {
    if (
      !window.confirm(
        `Megerősíted a kifizetést? (${formatPayMonth(salary.pay_month)})`
      )
    )
      return;
    setPayingId(salary._id);
    try {
      const { data } = await api.post(`/salaries/${salary._id}/pay`);
      upsertSalary(data);
      // Refresh bonuses + commissions: paying a salary cascades to attached items.
      fetchBonuses();
      fetchCommissions();
      toast.success("Bér kifizetve.");
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) ||
          "Nem sikerült kifizetésre állítani."
      );
    } finally {
      setPayingId(null);
    }
  };

  const upsertDeduction = (saved) => {
    if (!saved) return;
    setDeductions((prev) => {
      const idx = prev.findIndex((d) => d._id === saved._id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], ...saved };
        return next;
      }
      return [saved, ...prev].sort((a, b) =>
        (b.due_start || "").localeCompare(a.due_start || "")
      );
    });
  };

  const onDeleteDeduction = async (deduction) => {
    // Levonás törlése le van tiltva — pénzügyi integritás miatt csak a státusz
    // állítható "lejárt"-ra a szerkesztő dialogban.
    toast.error(
      "Levonást nem lehet törölni. Lejárt levonásnál állítsd a státuszt 'lejárt'-ra."
    );
    void deduction;
  };

  const upsertBonus = (saved) => {
    if (!saved) return;
    setBonuses((prev) => {
      const idx = prev.findIndex((b) => b._id === saved._id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], ...saved };
        return next;
      }
      return [saved, ...prev].sort((a, b) =>
        (b.issued_at || "").localeCompare(a.issued_at || "")
      );
    });
  };

  const onApproveBonus = async (bonus) => {
    setApprovingBonusId(bonus._id);
    try {
      const { data } = await api.post(`/bonuses/${bonus._id}/approve`);
      upsertBonus(data);
      toast.success("Bónusz jóváhagyva.");
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) ||
          "Nem sikerült jóváhagyni."
      );
    } finally {
      setApprovingBonusId(null);
    }
  };

  const onRejectBonus = async (bonus, reason) => {
    setRejectingBonusId(bonus._id);
    try {
      const { data } = await api.post(`/bonuses/${bonus._id}/reject`, {
        reject_reason: reason,
      });
      upsertBonus(data);
      toast.success("Bónusz elutasítva.");
      return true;
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) ||
          "Nem sikerült elutasítani."
      );
      return false;
    } finally {
      setRejectingBonusId(null);
    }
  };

  const onDeleteBonus = async (bonus) => {
    if (
      !window.confirm(
        `Biztosan törlöd a ${Number(bonus.amount || 0).toLocaleString("hu-HU")} Ft bónuszt?`
      )
    )
      return;
    try {
      await api.delete(`/bonuses/${bonus._id}`);
      setBonuses((prev) => prev.filter((b) => b._id !== bonus._id));
      toast.success("Bónusz törölve.");
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) || "Nem sikerült törölni."
      );
    }
  };

  const upsertCommission = (saved) => {
    if (!saved) return;
    setCommissions((prev) => {
      const idx = prev.findIndex((c) => c._id === saved._id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], ...saved };
        return next;
      }
      return [saved, ...prev].sort((a, b) =>
        (b.issued_at || "").localeCompare(a.issued_at || "")
      );
    });
  };

  const onApproveCommission = async (commission) => {
    setApprovingCommissionId(commission._id);
    try {
      const { data } = await api.post(`/commissions/${commission._id}/approve`);
      upsertCommission(data);
      toast.success("Jutalék jóváhagyva.");
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) ||
          "Nem sikerült jóváhagyni."
      );
    } finally {
      setApprovingCommissionId(null);
    }
  };

  const onRejectCommission = async (commission, reason) => {
    setRejectingCommissionId(commission._id);
    try {
      const { data } = await api.post(`/commissions/${commission._id}/reject`, {
        reject_reason: reason,
      });
      upsertCommission(data);
      toast.success("Jutalék elutasítva.");
      return true;
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) ||
          "Nem sikerült elutasítani."
      );
      return false;
    } finally {
      setRejectingCommissionId(null);
    }
  };

  const onDeleteCommission = async (commission) => {
    if (
      !window.confirm(
        `Biztosan törlöd a ${Number(commission.amount || 0).toLocaleString("hu-HU")} Ft jutalékot?`
      )
    )
      return;
    try {
      await api.delete(`/commissions/${commission._id}`);
      setCommissions((prev) => prev.filter((c) => c._id !== commission._id));
      toast.success("Jutalék törölve.");
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) || "Nem sikerült törölni."
      );
    }
  };

  // ---------------- Workwear handlers ----------------
  const upsertWorkwear = (saved) => {
    if (!saved) return;
    setWorkwear((prev) => {
      const idx = prev.findIndex((w) => w._id === saved._id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], ...saved };
        return next;
      }
      return [saved, ...prev].sort((a, b) =>
        (b.issued_at || "").localeCompare(a.issued_at || "")
      );
    });
  };

  const onReturnWorkwear = async (item, returnNotes) => {
    setReturningWorkwearId(item._id);
    try {
      const { data } = await api.post(`/workwear/${item._id}/return`, {
        return_notes: returnNotes,
      });
      upsertWorkwear(data);
      toast.success("Munkaruha visszavéve.");
      return true;
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) ||
          "Nem sikerült visszavenni."
      );
      return false;
    } finally {
      setReturningWorkwearId(null);
    }
  };

  const onDeleteWorkwear = async (item) => {
    if (!window.confirm("Biztosan törlöd a munkaruha tételt?")) return;
    try {
      await api.delete(`/workwear/${item._id}`);
      setWorkwear((prev) => prev.filter((w) => w._id !== item._id));
      toast.success("Munkaruha tétel törölve.");
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) || "Nem sikerült törölni."
      );
    }
  };

  // ---------------- Disciplinary handlers ----------------
  const upsertDisciplinary = (saved) => {
    if (!saved) return;
    setDisciplinaries((prev) => {
      const idx = prev.findIndex((d) => d._id === saved._id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], ...saved };
        return next;
      }
      return [saved, ...prev].sort((a, b) =>
        (b.issued_at || "").localeCompare(a.issued_at || "")
      );
    });
  };

  const onDeleteDisciplinary = async (item) => {
    if (
      !window.confirm(
        `Biztosan visszavonod a "${item.reason}" fegyelmi bejegyzést?`
      )
    )
      return;
    try {
      const { data } = await api.delete(`/disciplinaries/${item._id}`);
      setDisciplinaries((prev) => prev.filter((d) => d._id !== item._id));
      toast.success("Fegyelmi bejegyzés visszavonva.");
      // Cascade: a fegyelmi miatt elutasított bónuszok/jutalékok visszakerülnek
      // "Jóváhagyásra vár" státuszba.
      const bonusRev = data?.cascade_bonuses_reverted || 0;
      const commRev = data?.cascade_commissions_reverted || 0;
      if (bonusRev > 0 || commRev > 0) {
        fetchBonuses();
        fetchCommissions();
        const parts = [];
        if (bonusRev) parts.push(`${bonusRev} bónusz`);
        if (commRev) parts.push(`${commRev} jutalék`);
        toast.info(
          `Visszaállítva jóváhagyásra: ${parts.join(" és ")}.`
        );
      }
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) || "Nem sikerült törölni."
      );
    }
  };

  const onDelete = async () => {
    if (!window.confirm(`Biztosan törlöd "${fullName(employee)}" munkavállalót?`))
      return;
    setDeleting(true);
    try {
      await api.delete(`/employees/${id}`);
      toast.success("Munkavállaló törölve.");
      navigate("/employees");
    } catch (err) {
      toast.error(
        formatApiError(err?.response?.data?.detail) || "Nem sikerült törölni."
      );
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div
        data-testid="employee-detail-loading"
        className="flex h-[60vh] items-center justify-center text-sm text-muted-foreground"
      >
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Betöltés…
      </div>
    );
  }

  if (error || !employee) {
    return (
      <div
        data-testid="employee-detail-error"
        className="flex flex-col items-center gap-3 py-20 text-center"
      >
        <p className="text-sm text-muted-foreground">{error || "Nem található."}</p>
        <Button asChild variant="outline" size="sm">
          <Link to="/employees">
            <ArrowLeft className="h-3.5 w-3.5" /> Vissza a listához
          </Link>
        </Button>
      </div>
    );
  }

  // If the onboarding flow (data + 3 contracts signed + 5 ID photos) is not
  // yet complete, the entire profile is replaced by the focused onboarding
  // experience — the user explicitly asked: "semmi más ne legyen látható,
  // csak az onboarding". A 'Munkavállalók' back link is included inside the
  // onboarding component so the user can leave the profile.
  const onboardingDone = isOnboardingDone({ completeness, documents, employee });
  if (!onboardingDone) {
    return (
      <>
        <EmployeeOnboarding
          employee={employee}
          documents={documents}
          completeness={completeness}
          onChanged={() => {
            refreshDocuments();
            refreshCompleteness();
          }}
          onEditEmployee={() => setEditOpen(true)}
        />
        <EmployeeFormSheet
          open={editOpen}
          onOpenChange={setEditOpen}
          employee={employee}
          onSaved={(saved) => {
            if (saved) setEmployee((prev) => ({ ...prev, ...saved }));
            fetchEmployee();
          }}
        />
      </>
    );
  }

  return (
    <div data-testid="employee-detail-page" className="space-y-8">
      {/* Breadcrumb back */}
      <div className="flex items-center gap-3">
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="-ml-3 h-8 text-muted-foreground"
        >
          <Link to="/employees" data-testid="back-to-employees">
            <ArrowLeft className="h-3.5 w-3.5" /> Munkavállalók
          </Link>
        </Button>
      </div>

      {/* Profile header */}
      <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-5">
          <div className="grid h-16 w-16 place-items-center rounded-2xl bg-foreground text-background text-lg font-semibold">
            {initials(employee)}
          </div>
          <div className="space-y-2">
            <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              Munkavállalói adatlap
            </p>
            <h1
              data-testid="employee-name"
              className="text-2xl font-semibold tracking-tight sm:text-3xl"
            >
              {employee.titulus ? `${employee.titulus} ` : ""}
              {fullName(employee)}
            </h1>
            <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              <EmployeeStatusBadge value={employee.status} />
              {employee.position && <span>· {employee.position}</span>}
              {company && (
                <span className="inline-flex items-center gap-1.5">
                  · <Building2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                  <Link
                    to={`/companies/${company._id}`}
                    className="hover:text-foreground"
                  >
                    {company.name}
                  </Link>
                </span>
              )}
              {site && (
                <span className="inline-flex items-center gap-1.5">
                  · <MapPin className="h-3.5 w-3.5" strokeWidth={1.75} />
                  {site.name}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="employee-edit-button"
            variant="outline"
            onClick={() => setEditOpen(true)}
          >
            <Pencil className="h-4 w-4" /> Szerkesztés
          </Button>
          <Button
            data-testid="employee-delete-button"
            variant="outline"
            onClick={onDelete}
            disabled={deleting}
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" /> Törlés
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList data-testid="employee-detail-tabs">
          <TabsTrigger value="data" data-testid="tab-data">
            Adatok
          </TabsTrigger>
          <TabsTrigger value="employment" data-testid="tab-employment">
            Munkaviszony
          </TabsTrigger>
          <TabsTrigger value="operational" data-testid="tab-operational">
            Operatív
          </TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">
            Dokumentumok
          </TabsTrigger>
          <TabsTrigger value="salaries" data-testid="tab-salaries">
            Bér információk ({salaries.length})
          </TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">
            Jegyzetek
          </TabsTrigger>
          <TabsTrigger value="portal" data-testid="tab-portal">
            Portál hozzáférés
          </TabsTrigger>
        </TabsList>

        <TabsContent value="data" className="space-y-6">
          {/* Detail layout — single column, full-width cards (Stripe-style) */}
          <div className="space-y-6">
            <DetailCard title="Személyes adatok" testId="card-personal">
              <DataGrid cols={3}>
                <DataRow label="Titulus" value={employee.titulus} testId="d-titulus" />
                <DataRow label="Nem" value={genderLabel(employee.gender)} testId="d-gender" />
                <DataRow
                  label="Állampolgárság"
                  value={employee.citizenship}
                  testId="d-citizenship"
                />
                <DataRow label="Vezetéknév" value={employee.last_name} testId="d-last-name" />
                <DataRow label="Keresztnév" value={employee.first_name} testId="d-first-name" />
                <DataRow
                  label="Anyja neve"
                  value={employee.mother_name}
                  testId="d-mother-name"
                />
                <DataRow
                  label="Születési vezetéknév"
                  value={employee.birth_last_name}
                  testId="d-birth-last-name"
                />
                <DataRow
                  label="Születési keresztnév"
                  value={employee.birth_first_name}
                  testId="d-birth-first-name"
                />
                <DataRow
                  label="Születési hely"
                  value={employee.birth_place}
                  testId="d-birth-place"
                />
                <DataRow
                  label="Születési dátum"
                  value={formatDateHu(employee.birth_date)}
                  icon={<Calendar className="h-3.5 w-3.5" strokeWidth={1.75} />}
                  testId="d-birth-date"
                />
                <DataRow
                  label="Állapot"
                  value={<EmployeeStatusBadge value={employee.status} />}
                  testId="d-status"
                />
                <DataRow
                  label="Beosztás"
                  value={employee.position}
                  testId="d-position"
                />
              </DataGrid>
            </DetailCard>

            <div className="grid gap-6 lg:grid-cols-2">
              <DetailCard title="POS Adatok" testId="card-pos">
                <DataGrid cols={2}>
                  <DataRow
                    label="POS ID"
                    value={employee.pos_id}
                    mono
                    testId="d-pos-id"
                  />
                  <PosPasswordRow value={employee.pos_password} />
                </DataGrid>
              </DetailCard>

              <DetailCard title="Elérhetőség" testId="card-contact">
                <DataGrid cols={2}>
                  <DataRow
                    label="E-mail cím"
                    value={employee.email}
                    mono
                    icon={<Mail className="h-3.5 w-3.5" strokeWidth={1.75} />}
                    testId="d-email"
                  />
                  <DataRow
                    label="Telefon"
                    value={employee.phone}
                    icon={<Phone className="h-3.5 w-3.5" strokeWidth={1.75} />}
                    testId="d-phone"
                  />
                </DataGrid>
              </DetailCard>
            </div>

            <DetailCard title="Címek" testId="card-addresses">
              <DataGrid cols={2}>
                <DataRow
                  label="Lakcím"
                  value={employee.address}
                  testId="d-address"
                  multiline
                />
                <DataRow
                  label="Levelezési cím"
                  value={employee.mailing_address}
                  testId="d-mailing-address"
                  multiline
                />
              </DataGrid>
            </DetailCard>

            <DetailCard title="Azonosító okmányok" testId="card-ids">
              <DataGrid cols={3}>
                <DataRow
                  label="TAJ szám"
                  value={employee.taj}
                  mono
                  icon={<IdCardIcon className="h-3.5 w-3.5" strokeWidth={1.75} />}
                  testId="d-taj"
                />
                <DataRow
                  label="Adóazonosító"
                  value={employee.tax_id}
                  mono
                  testId="d-tax-id"
                />
                <DataRow
                  label="Személyi ig. szám"
                  value={employee.id_card_number}
                  mono
                  testId="d-id-card-number"
                />
              </DataGrid>
            </DetailCard>

            <FinancialCard
              employee={employee}
              onEmployeeChange={(updated) =>
                setEmployee((prev) => ({ ...prev, ...updated }))
              }
            />
          </div>

      {/* Meta footer */}
      <div className="rounded-xl border border-border bg-[hsl(var(--sipx-elevated))] p-4 text-[11px] text-muted-foreground">
        <span className="font-mono">{employee._id}</span>
        <span className="mx-2">·</span>
        Létrehozva: {formatDateHu(employee.created_at)}
        {employee.updated_at && (
          <>
            <span className="mx-2">·</span>
            Módosítva: {formatDateHu(employee.updated_at)}
          </>
        )}
      </div>
        </TabsContent>

        <TabsContent value="employment" className="space-y-4">
          <EmploymentTab
            employee={employee}
            onEmployeeChange={(updated) =>
              setEmployee((prev) => ({ ...prev, ...updated }))
            }
            deductions={deductions}
            loading={deductionsLoading}
            onCreate={() => {
              setEditingDeduction(null);
              setDeductionOpen(true);
            }}
            onDeductionChanged={(saved) => {
              setDeductions((prev) =>
                prev.map((d) => (d._id === saved._id ? saved : d))
              );
            }}
          />
        </TabsContent>

        <TabsContent value="operational" className="space-y-4">
          <OperationalTab
            bonuses={bonuses}
            bonusesLoading={bonusesLoading}
            approvingBonusId={approvingBonusId}
            rejectingBonusId={rejectingBonusId}
            onCreateBonus={() => setBonusOpen(true)}
            onApproveBonus={onApproveBonus}
            onRejectBonus={onRejectBonus}
            onDeleteBonus={onDeleteBonus}
            commissions={commissions}
            commissionsLoading={commissionsLoading}
            approvingCommissionId={approvingCommissionId}
            rejectingCommissionId={rejectingCommissionId}
            onCreateCommission={() => setCommissionOpen(true)}
            onApproveCommission={onApproveCommission}
            onRejectCommission={onRejectCommission}
            onDeleteCommission={onDeleteCommission}
            workwear={workwear}
            workwearLoading={workwearLoading}
            returningWorkwearId={returningWorkwearId}
            onCreateWorkwear={() => setWorkwearOpen(true)}
            onReturnWorkwear={onReturnWorkwear}
            onDeleteWorkwear={onDeleteWorkwear}
            disciplinaries={disciplinaries}
            disciplinariesLoading={disciplinariesLoading}
            onCreateDisciplinary={() => setDisciplinaryOpen(true)}
            onDeleteDisciplinary={onDeleteDisciplinary}
          />
        </TabsContent>

        <TabsContent value="documents" className="space-y-4">
          <EmployeeDocumentsTab
            employeeId={employee._id}
            employee={employee}
          />
        </TabsContent>

        <TabsContent value="salaries" className="space-y-4">
          <SalariesTab
            salaries={salaries}
            loading={salariesLoading}
            company={company}
            site={site}
            payingId={payingId}
            onCreate={() => {
              setSalaryOpen(true);
            }}
            onAccount={onOpenAccounting}
            onPay={onMarkPaid}
            onDelete={onDeleteSalary}
          />
        </TabsContent>

        <TabsContent value="notes" className="space-y-4">
          <NotesTab employeeId={employee._id} />
        </TabsContent>

        <TabsContent value="portal" className="space-y-4">
          <PortalAccessTab employee={employee} onChanged={fetchEmployee} />
        </TabsContent>
      </Tabs>

      <EmployeeFormSheet
        open={editOpen}
        onOpenChange={setEditOpen}
        employee={employee}
        onSaved={(saved) => {
          if (saved) setEmployee((prev) => ({ ...prev, ...saved }));
          fetchEmployee();
        }}
      />
      <SalaryFormSheet
        open={salaryOpen}
        onOpenChange={setSalaryOpen}
        employee={employee}
        company={company}
        site={site}
        onSaved={(saved) => {
          upsertSalary(saved);
          fetchSalaries();
        }}
      />
      <AccountingFormDialog
        open={accountingOpen}
        onOpenChange={setAccountingOpen}
        salary={accountingSalary}
        employee={employee}
        onSaved={(saved) => {
          upsertSalary(saved);
        }}
      />
      <DeductionFormDialog
        open={deductionOpen}
        onOpenChange={setDeductionOpen}
        employee={employee}
        deduction={editingDeduction}
        onSaved={(saved) => {
          upsertDeduction(saved);
          fetchDeductions();
        }}
      />
      <BonusFormDialog
        open={bonusOpen}
        onOpenChange={setBonusOpen}
        employee={employee}
        onSaved={(saved) => {
          upsertBonus(saved);
          fetchBonuses();
        }}
      />
      <CommissionFormDialog
        open={commissionOpen}
        onOpenChange={setCommissionOpen}
        employee={employee}
        onSaved={(saved) => {
          upsertCommission(saved);
          fetchCommissions();
        }}
      />
      <WorkwearFormDialog
        open={workwearOpen}
        onOpenChange={setWorkwearOpen}
        employee={employee}
        onSaved={(saved) => {
          upsertWorkwear(saved);
          fetchWorkwear();
        }}
      />
      <DisciplinaryFormDialog
        open={disciplinaryOpen}
        onOpenChange={setDisciplinaryOpen}
        employee={employee}
        onSaved={(saved) => {
          upsertDisciplinary(saved);
          fetchDisciplinaries();
          // Cascade: fizetetlen bónuszok és jutalékok átkerülnek "rejected" státuszba.
          if (
            saved &&
            ((saved.cascade_bonuses_rejected || 0) > 0 ||
              (saved.cascade_commissions_rejected || 0) > 0)
          ) {
            fetchBonuses();
            fetchCommissions();
            const parts = [];
            if (saved.cascade_bonuses_rejected) {
              parts.push(`${saved.cascade_bonuses_rejected} bónusz`);
            }
            if (saved.cascade_commissions_rejected) {
              parts.push(`${saved.cascade_commissions_rejected} jutalék`);
            }
            toast.info(
              `Fegyelmi miatt elutasítva: ${parts.join(" és ")}.`
            );
          }
        }}
      />
    </div>
  );
}

function DetailCard({ title, children, testId, className }) {
  return (
    <section
      data-testid={testId}
      className={cn(
        "rounded-xl border border-border bg-[hsl(var(--sipx-elevated))]",
        className
      )}
    >
      <header className="border-b border-border px-5 py-3">
        <h3 className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
          {title}
        </h3>
      </header>
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

function DataGrid({ cols = 2, children }) {
  return (
    <dl
      className={cn(
        "grid gap-x-6 gap-y-3",
        cols === 1 && "grid-cols-1",
        cols === 2 && "grid-cols-1 sm:grid-cols-2",
        cols === 3 && "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
      )}
    >
      {children}
    </dl>
  );
}

function DataRow({ label, value, icon, mono, multiline, testId }) {
  const isEmpty = value === undefined || value === null || value === "";
  return (
    <div className="flex flex-col gap-0.5" data-testid={testId}>
      <dt className="text-[10.5px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
        {label}
      </dt>
      <dd
        className={cn(
          "inline-flex items-center gap-1.5 text-sm",
          isEmpty && "text-muted-foreground/70",
          mono && "font-mono text-[12.5px]",
          multiline && "whitespace-pre-line"
        )}
      >
        {icon && !isEmpty && (
          <span className="text-muted-foreground">{icon}</span>
        )}
        {isEmpty ? "—" : value}
      </dd>
    </div>
  );
}

function PosPasswordRow({ value }) {
  const [show, setShow] = useState(false);
  const isEmpty = !value;
  return (
    <div className="flex flex-col gap-0.5" data-testid="d-pos-password">
      <dt className="text-[10.5px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
        POS jelszó
      </dt>
      <dd
        className={cn(
          "inline-flex items-center gap-2 text-sm",
          isEmpty && "text-muted-foreground/70",
        )}
      >
        <LockIcon className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.75} />
        <span className={cn("font-mono text-[12.5px]", !show && !isEmpty && "tracking-[0.18em]")}>
          {isEmpty ? "—" : show ? value : "•".repeat(Math.max(6, String(value).length))}
        </span>
        {!isEmpty && (
          <button
            type="button"
            onClick={() => setShow((v) => !v)}
            aria-label={show ? "Jelszó elrejtése" : "Jelszó megjelenítése"}
            className="ml-1 grid h-6 w-6 place-items-center rounded-md text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
            data-testid="toggle-pos-password"
          >
            {show ? (
              <EyeOff className="h-3.5 w-3.5" strokeWidth={1.75} />
            ) : (
              <Eye className="h-3.5 w-3.5" strokeWidth={1.75} />
            )}
          </button>
        )}
      </dd>
    </div>
  );
}


function FinancialCard({ employee, onEmployeeChange }) {
  const [busy, setBusy] = useState(false);
  const isCash = employee.payment_method === "cash";
  const isTransfer = employee.payment_method === "transfer";
  // Back-compat: if legacy bool flag is set, map it to approved/pending.
  const legacyApproved =
    typeof employee.cash_payment_approved === "boolean"
      ? employee.cash_payment_approved
      : null;
  const status =
    employee.cash_payment_status ||
    (legacyApproved === true
      ? "approved"
      : legacyApproved === false && isCash
        ? "pending"
        : isCash
          ? "pending"
          : null);

  const decidedBy =
    employee.cash_payment_decided_by_name ||
    employee.cash_payment_approved_by_name;
  const decidedAt =
    employee.cash_payment_decided_at || employee.cash_payment_approved_at;

  const callDecision = async (action, successMsg) => {
    if (!window.confirm(`${successMsg}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/employees/${employee._id}/cash-payment/${action}`
      );
      onEmployeeChange(data);
      toast.success(`${successMsg}.`);
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) || "Művelet sikertelen."
      );
    } finally {
      setBusy(false);
    }
  };

  const renderStatusBadge = () => {
    if (!isCash) return null;
    if (status === "approved") {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[12px] font-medium text-emerald-700 dark:text-emerald-300">
          Jóváhagyva
        </span>
      );
    }
    if (status === "rejected") {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-rose-500/40 bg-rose-500/10 px-2 py-0.5 text-[12px] font-medium text-rose-700 dark:text-rose-300">
          Elutasítva
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[12px] font-medium text-amber-700 dark:text-amber-300">
        Jóváhagyásra vár
      </span>
    );
  };

  return (
    <DetailCard title="Pénzügyi adatok" testId="card-financial">
      <DataGrid cols={3}>
        <DataRow
          label="Bankszámlaszám"
          value={employee.bank_account_number}
          mono
          testId="d-bank-account"
        />
        <DataRow
          label="Bank neve"
          value={employee.bank_name}
          testId="d-bank-name"
        />
        <DataRow
          label="Kifizetés módja"
          value={isCash ? "Készpénz" : isTransfer ? "Átutalás" : null}
          testId="d-payment-method"
        />
        {isCash && (
          <DataRow
            label="Készpénz státusz"
            value={renderStatusBadge()}
            testId="d-cash-status"
          />
        )}
      </DataGrid>

      {isCash && (status === "approved" || status === "rejected") && decidedBy && (
        <p className="mt-3 text-[11.5px] text-muted-foreground">
          {status === "approved" ? "Jóváhagyta" : "Elutasította"}:{" "}
          <b className="text-foreground">{decidedBy}</b>
          {decidedAt && <> · {formatDateHu(decidedAt)}</>}
        </p>
      )}

      {isCash && (
        <div className="mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-border/70 pt-4">
          {status === "pending" || status == null ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => callDecision("reject", "Készpénz elutasítva")}
                disabled={busy}
                data-testid="cash-payment-reject"
                className="border-rose-500/30 text-rose-700 hover:bg-rose-500/10 hover:text-rose-800 dark:text-rose-300"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                Elutasítás
              </Button>
              <Button
                size="sm"
                onClick={() => callDecision("approve", "Készpénz jóváhagyva")}
                disabled={busy}
                data-testid="cash-payment-approve"
                className="bg-emerald-600 text-white hover:bg-emerald-700"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                Jóváhagyás
              </Button>
            </>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => callDecision("reset", "Státusz visszaállítva (jóváhagyásra vár)")}
              disabled={busy}
              data-testid="cash-payment-reset"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Döntés visszavonása
            </Button>
          )}
        </div>
      )}
    </DetailCard>
  );
}
